import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  BookOpen, TrendingUp, TrendingDown, Shield, Target, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, Eye, Activity
} from 'lucide-react';

// FAQ Data for Schema
const faqData = [
  {
    question: "Kripto sinyali yatırım tavsiyesi midir?",
    answer: "Hayır. Kripto sinyalleri teknik analiz senaryosudur, yatırım tavsiyesi değildir. Her işlemde kendi araştırmanızı yapmanız önerilir."
  },
  {
    question: "TP ne demek?",
    answer: "TP (Take Profit) kar alma seviyesi demektir. Fiyatın ulaşması beklenen hedef noktasıdır."
  },
  {
    question: "Stop Loss ne işe yarar?",
    answer: "Stop Loss, senaryonun geçersiz sayıldığı risk sınırıdır. Zarar etmek için değil, riski sınırlamak için kullanılır."
  },
  {
    question: "LONG ve SHORT farkı nedir?",
    answer: "LONG yükseliş beklentisi, SHORT düşüş beklentisi anlamına gelir. Yön farkıdır, garanti vermez."
  },
  {
    question: "Sinyaller her zaman doğru çıkar mı?",
    answer: "Hayır. Kripto sinyalleri olasılık temellidir. Hiçbir sinyal %100 doğruluk garantisi vermez."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Kripto Sinyali Nedir? TP, Stop ve LONG/SHORT Mantığı",
    "description": "Kripto sinyali ne demek? TP, Stop Loss, LONG ve SHORT kavramlarını basitçe öğrenin.",
    "author": {
      "@type": "Organization",
      "name": "Borsamex"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://borsamex.com/logo512.png"
      }
    },
    "datePublished": "2025-12-24",
    "dateModified": "2025-12-24",
    "mainEntityOfPage": "https://borsamex.com/rehber/kripto-sinyali-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const KriptoSinyaliNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Kripto Sinyali Nedir? TP, Stop ve LONG/SHORT Mantığı | Borsamex"
        description="Kripto sinyali ne demek? TP, Stop Loss, LONG ve SHORT kavramlarını basitçe öğrenin. Trading sinyalleri nasıl çalışır adım adım keşfedin."
        keywords="kripto sinyali nedir, trading sinyali ne demek, tp nedir, stop loss nedir, long short nedir, kripto sinyal nasıl çalışır"
        canonical="https://borsamex.com/rehber/kripto-sinyali-nedir"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Kripto Sinyali Nedir?</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">Temel</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>5 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Kripto Sinyali Nedir?
            </h1>
            
            {/* Featured Snippet Target */}
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">Kripto sinyali</strong>, belirli bir varlık için olası işlem senaryosunu gösteren teknik analiz çıktısıdır. 
                Bir sinyal genellikle <strong className="text-brand-green">giriş fiyatı</strong>, <strong className="text-green-400">hedef seviyeleri (TP)</strong> ve <strong className="text-red-400">risk sınırı (Stop Loss)</strong> içerir.
              </p>
              <p className="text-slate-400 mt-4">
                Amaç, piyasa hareketlerini tahmin etmek değil, olası senaryoları yapılandırmaktır.
              </p>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="prose prose-invert prose-slate max-w-none"
          >
            
            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-purple-400" />
                </div>
                Kripto Sinyali Ne Anlama Gelir?
              </h2>
              
              <p className="text-slate-300 mb-4">Bir kripto sinyali şunu söyler:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Hangi varlık izleniyor</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Hangi yönde senaryo var (LONG / SHORT)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Olası hedef seviyeleri</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk sınırı</span>
                </li>
              </ul>
              
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-4">
                  <p className="text-amber-200 text-sm flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    Bu yapı, yatırım tavsiyesi değil, teknik senaryo çerçevesidir.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-blue-400" />
                </div>
                Kripto Sinyali İçinde Neler Bulunur?
              </h2>

              {/* Giriş Seviyesi */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Giriş Seviyesi</h3>
                <p className="text-slate-300">
                  Pozisyonun açılmasının düşünüldüğü fiyat bölgesi.
                </p>
              </div>

              {/* TP Seviyeleri */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-3">TP (Take Profit) Seviyeleri</h3>
                <p className="text-slate-300 mb-4">
                  Fiyatın ulaşması beklenen olası hedef noktaları.
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-green-900/20 rounded-lg border border-green-500/20">
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">TP1</Badge>
                    <span className="text-slate-300">İlk hedef</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">TP2</Badge>
                    <span className="text-slate-300">Ana hedef</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-cyan-900/20 rounded-lg border border-cyan-500/20">
                    <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">TP3</Badge>
                    <span className="text-slate-300">Güçlü trend senaryosu</span>
                  </div>
                </div>
              </div>

              {/* Stop Loss */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Stop Loss Seviyesi</h3>
                <p className="text-slate-300 mb-3">
                  Senaryonun geçersiz sayıldığı risk sınırı.
                </p>
                <div className="flex items-center gap-3 p-3 bg-red-900/20 rounded-lg border border-red-500/20">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">STOP</Badge>
                  <span className="text-slate-300">Amaç zarar etmek değil, riski sınırlamaktır.</span>
                </div>
              </div>
            </section>

            {/* Section 3 - LONG/SHORT */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">BTC-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Giriş: $67,450 | TP1: $69,200 | TP2: $71,000 | Stop: $66,100
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Sniper Engine tarafından üretildi</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex'in AI sistemi, 200+ teknik göstergeyi analiz ederek otomatik sinyal üretir.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Tüm Sinyalleri İncele <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Section 3 - LONG/SHORT */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-green-400" />
                </div>
                LONG ve SHORT Ne Demek?
              </h2>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendingUp className="h-6 w-6 text-green-400" />
                      <h3 className="font-bold text-white">LONG Senaryosu</h3>
                    </div>
                    <p className="text-slate-300 text-sm">Fiyatın yükselmesi beklentisi.</p>
                  </CardContent>
                </Card>

                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendingDown className="h-6 w-6 text-red-400" />
                      <h3 className="font-bold text-white">SHORT Senaryosu</h3>
                    </div>
                    <p className="text-slate-300 text-sm">Fiyatın düşmesi beklentisi.</p>
                  </CardContent>
                </Card>
              </div>

              <p className="text-slate-400 text-sm">
                Bu terimler yönü ifade eder, garanti vermez.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-amber-400" />
                </div>
                Kripto Sinyalleri Nasıl Üretilir?
              </h2>
              
              <p className="text-slate-300 mb-4">Profesyonel sistemlerde sinyaller şu veriler kullanılarak oluşturulur:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Teknik göstergeler (RSI, MACD, Bollinger...)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Volatilite hesapları</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Trend yapısı</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Likidite davranışı</span>
                </li>
              </ul>
            </section>

            {/* Section 5 - Borsamex */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex Sinyal Mantığı
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex sinyalleri:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Algoritmik analiz motoru tarafından üretilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Giriş, TP ve Stop seviyeleri içerir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk yapısını önceliklendirir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>AI yalnızca açıklama ve bağlam sunar</span>
                </li>
              </ul>

              <div className="flex gap-4">
                <Link to="/ai-kripto-sinyalleri">
                  <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    AI Sinyalleri Hakkında →
                  </Button>
                </Link>
              </div>
            </section>

            {/* İlgili Konular - Internal Links */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                İlgili Konular
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
                  <Card className="border-green-500/20 bg-slate-900/50 hover:border-green-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Target className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</p>
                          <p className="text-xs text-slate-500">Hedef ve risk seviyelerini öğren</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/long-ve-short-ne-demek" className="group">
                  <Card className="border-blue-500/20 bg-slate-900/50 hover:border-blue-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-blue-400 transition-colors">LONG ve SHORT Ne Demek?</p>
                          <p className="text-xs text-slate-500">Yön kavramlarını anla</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/kripto-sinyali-nasil-okunur" className="group">
                  <Card className="border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Eye className="h-5 w-5 text-amber-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-amber-400 transition-colors">Sinyal Nasıl Okunur?</p>
                          <p className="text-xs text-slate-500">Pratik sinyal okuma rehberi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
                  <Card className="border-red-500/20 bg-slate-900/50 hover:border-red-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-red-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-red-400 transition-colors">Risk Yönetimi</p>
                          <p className="text-xs text-slate-500">Sermaye koruma stratejileri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            {/* Önemli Not */}
            <section className="mb-12">
              <Card className="border-red-500/30 bg-red-950/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-8 w-8 text-red-400 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-red-400 mb-2">Önemli Not</h3>
                      <p className="text-slate-300">
                        Kripto sinyalleri kesinlik içermez. Tüm işlemler risk barındırır. 
                        Sadece kaybetmeyi göze alabileceğiniz miktarla işlem yapın.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="Kripto Sinyali Nedir? Detayli Rehber"
              url="/rehber/kripto-sinyali-nedir"
              hashtags="#kripto #sinyal #bitcoin #trading"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Tüm Rehberler
            </Link>
            <Link to="/rehber/tp-ve-stop-loss-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: TP ve Stop Loss Nedir?
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-purple-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Öğrendiklerini Uygulamaya Hazır mısın?
            </h3>
            <p className="text-slate-400 mb-6">
              Ücretsiz kaydol ve canlı sinyalleri takip etmeye başla.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default KriptoSinyaliNedirPage;
